import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Select, message } from 'antd';
import Breadcrumbs from '../../components/Breadcrumbs';
import Pagination from '../../components/Pagination';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal';
import {
  PlusIcon,
  CaretIcon,
  RefreshIcon,
  EditLineIcon,
  TrashIcon,
  OrgBuildingIcon,
} from '../../assets/icons';
import {
  useGetResourcesQuery,
  useGetResourceFiltersQuery,
  useDeleteResourceMutation,
  type Resource,
} from '../../features/resources/resourcesApi';
import { CATEGORIES, SUB_CATEGORIES, RESOURCE_TYPES } from './AddResourcePage';

/* ------------------------------------------------------------------ *
 * Resource Directory — listing (sidebar → Resources Directory).
 * Figma 1682-4737. Backed by GET /resource/ (server-side filter +
 * pagination) with options from GET /resource/filters/.
 * ------------------------------------------------------------------ */

/** Shared grid template so header + body rows stay aligned. */
const GRID =
  'grid grid-cols-[minmax(230px,1.7fr)_120px_minmax(150px,1.2fr)_minmax(150px,1.2fr)_110px_96px] items-center';

const caret = <CaretIcon className="size-3.5 text-[#64748b]" />;

function ResourceRow({
  row,
  onEdit,
  onDelete,
}: {
  row: Resource;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div className={[GRID, 'border-t border-[#f3f4f6]'].join(' ')}>
      {/* Name + icon */}
      <div className="flex items-center gap-3 px-6 py-4">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-[#dbeafe]/70 text-brand">
          <OrgBuildingIcon className="size-4" />
        </span>
        <p className="truncate text-xs font-bold leading-4 text-[#1e293b]">{row.name}</p>
      </div>

      {/* Category */}
      <div className="px-6 py-4 text-xs font-medium text-[#475569]">{row.category || '—'}</div>

      {/* Sub category */}
      <div className="px-6 py-4 text-xs font-medium text-[#475569]">{row.sub_category || '—'}</div>

      {/* Type + ownership qualifier */}
      <div className="px-6 py-4 text-xs leading-4">
        <span className="font-medium text-[#1e293b]">{row.resource_type || '—'} </span>
        {row.ownership ? <span className="text-[#94a3b8]">({row.ownership})</span> : null}
      </div>

      {/* Location */}
      <div className="px-6 py-4 text-xs font-medium text-[#475569]">
        {row.location_level || row.area_code || '—'}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-2 px-6 py-4">
        <button
          type="button"
          onClick={onEdit}
          aria-label={`Edit ${row.name}`}
          className="rounded p-1 text-[#6b7280] transition-colors hover:bg-brand-shade hover:text-brand"
        >
          <EditLineIcon className="size-4" />
        </button>
        <button
          type="button"
          onClick={onDelete}
          aria-label={`Delete ${row.name}`}
          className="rounded p-1 text-[#6b7280] transition-colors hover:bg-red-50 hover:text-red-500"
        >
          <TrashIcon className="size-4" />
        </button>
      </div>
    </div>
  );
}

export default function ResourceDirectoryPage() {
  const navigate = useNavigate();
  const [category, setCategory] = useState<string>();
  const [subCategory, setSubCategory] = useState<string>();
  const [type, setType] = useState<string>();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => setPage(1), [category, subCategory, type, pageSize]);

  const { data: filters } = useGetResourceFiltersQuery();
  const { data, isFetching, isError } = useGetResourcesQuery({
    category,
    sub_category: subCategory,
    resource_type: type,
    page,
    pageSize,
  });
  const [deleteResource, { isLoading: deleting }] = useDeleteResourceMutation();
  const [toDelete, setToDelete] = useState<Resource | null>(null);

  const rows = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // Filter options come from the backend, falling back to the Add-form seed lists.
  const categoryOpts = filters?.categories?.length ? filters.categories : CATEGORIES;
  const subCategoryOpts = filters?.sub_categories?.length ? filters.sub_categories : SUB_CATEGORIES;
  const resourceTypeOpts = filters?.resource_types?.length ? filters.resource_types : RESOURCE_TYPES;

  const resetFilters = () => {
    setCategory(undefined);
    setSubCategory(undefined);
    setType(undefined);
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await deleteResource(toDelete.id).unwrap();
      message.success('Resource deleted.');
      setToDelete(null);
    } catch {
      message.error('Could not delete the resource.');
    }
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Header + CTA */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <Breadcrumbs items={[{ label: 'Dashboard', to: '/admin' }, { label: 'Resource Directory' }]} />
          <h1 className="pt-1 text-2xl font-semibold leading-8 tracking-[-0.6px] text-ink">
            Resource Directory
          </h1>
          <p className="text-sm leading-5 text-ink-secondary">
            Find and manage resources like schools, hospitals, religious places and more in your
            constituency.
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/admin/resource-directory/new')}
          className="flex shrink-0 items-center gap-1.5 rounded-lg bg-brand px-4 py-2.5 text-sm font-medium text-white shadow-card transition-colors hover:bg-brand-dark"
        >
          <PlusIcon className="size-4" />
          Add Resource
        </button>
      </div>

      {/* Filter toolbar */}
      <div className="rounded-xl border border-[#e5e7eb] bg-white p-[17px] shadow-card">
        <div className="flex flex-wrap items-center gap-3">
          <Select
            className="min-w-[200px] flex-1"
            allowClear
            placeholder="Category"
            value={category}
            onChange={setCategory}
            options={categoryOpts.map((o) => ({ value: o, label: o }))}
            suffixIcon={caret}
          />
          <Select
            className="min-w-[200px] flex-1"
            allowClear
            placeholder="Sub-Category"
            value={subCategory}
            onChange={setSubCategory}
            options={subCategoryOpts.map((o) => ({ value: o, label: o }))}
            suffixIcon={caret}
          />
          <Select
            className="min-w-[200px] flex-1"
            allowClear
            placeholder="Resource Type"
            value={type}
            onChange={setType}
            options={resourceTypeOpts.map((o) => ({ value: o, label: o }))}
            suffixIcon={caret}
          />
          <button
            type="button"
            onClick={resetFilters}
            aria-label="Reset filters"
            className="rounded-lg border border-[#e5e7eb] bg-white p-2.5 text-[#6b7280] transition-colors hover:bg-brand-shade hover:text-brand"
          >
            <RefreshIcon className="size-4" />
          </button>
        </div>
      </div>

      {/* Resources table */}
      <div className="flex flex-col gap-4">
        <h2 className="text-lg font-semibold leading-7 text-ink">Resources</h2>
        <div className="overflow-hidden rounded-xl border border-[#e5e7eb] bg-white shadow-card">
          <div className="overflow-x-auto">
            <div className="min-w-[840px]">
              {/* Header */}
              <div className={[GRID, 'border-b border-[#f3f4f6] bg-[#f9fafb]/50'].join(' ')}>
                {(
                  [
                    ['NAME', 'justify-start'],
                    ['CATEGORY', 'justify-start'],
                    ['SUB CATEGORY', 'justify-start'],
                    ['TYPE', 'justify-start'],
                    ['LOCATION', 'justify-start'],
                    ['ACTIONS', 'justify-end text-right'],
                  ] as const
                ).map(([label, align]) => (
                  <div key={label} className={['flex px-6 py-[14px]', align].join(' ')}>
                    <span className="text-[11px] font-bold uppercase leading-[16.5px] tracking-[0.55px] text-[#6b7280]">
                      {label}
                    </span>
                  </div>
                ))}
              </div>

              {/* Body */}
              {isError ? (
                <p className="px-6 py-12 text-center text-sm text-red-500">
                  Could not load resources. Please try again.
                </p>
              ) : rows.length > 0 ? (
                rows.map((row) => (
                  <ResourceRow
                    key={row.id}
                    row={row}
                    onEdit={() => navigate(`/admin/resource-directory/${row.id}/edit`)}
                    onDelete={() => setToDelete(row)}
                  />
                ))
              ) : (
                <p className="px-6 py-12 text-center text-sm text-ink-secondary">
                  {isFetching ? 'Loading resources…' : 'No resources match your filters.'}
                </p>
              )}
            </div>
          </div>

          {/* Pagination */}
          <Pagination
            rangeStart={total === 0 ? 0 : (page - 1) * pageSize + 1}
            rangeEnd={Math.min(page * pageSize, total)}
            total={total}
            noun="resources"
            page={page}
            totalPages={totalPages}
            onChange={setPage}
            pageSize={pageSize}
            onPageSizeChange={(v) => {
              setPageSize(v);
              setPage(1);
            }}
          />
        </div>
      </div>

      <ConfirmDeleteModal
        open={toDelete !== null}
        title="Delete Resource"
        message={
          <>
            Are you sure you want to delete{' '}
            <span className="font-semibold text-ink">{toDelete?.name}</span>? This action cannot be
            undone.
          </>
        }
        loading={deleting}
        onClose={() => setToDelete(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
